// Casualty: unknown patients and merging their records (docs/plans/phase2-casualty.md §3).
// Descriptions and reasons go in request bodies only, never in a URL.
import { queryOptions } from '@tanstack/react-query';
import {
  patientMergeDetailSchema,
  patientMergeListSchema,
  registerUnknownPatientResultSchema,
  unidentifiedPatientListSchema,
  valueSetConceptSchema,
  type PatientMergeStatus,
  type registerUnknownPatientRequestSchema,
  type requestPatientMergeSchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

/** Refreshes every 30 seconds while the Casualty page is open, like the bed board. */
export const unidentifiedPatientsQuery = queryOptions({
  queryKey: ['casualty', 'unidentified'],
  queryFn: () =>
    api('GET', '/casualty/unknown-patients', { schema: unidentifiedPatientListSchema }),
  refetchInterval: 30_000,
});

/** The sex list, for people who register in casualty without the registration desk's rights. */
export const casualtySexOptionsQuery = queryOptions({
  queryKey: ['casualty', 'sex-options'],
  queryFn: () => api('GET', '/casualty/sex-options', { schema: z.array(valueSetConceptSchema) }),
  staleTime: 5 * 60_000,
});

export const registerUnknownPatient = (body: In<typeof registerUnknownPatientRequestSchema>) =>
  api('POST', '/casualty/unknown-patients', { body, schema: registerUnknownPatientResultSchema });

export const requestPatientMerge = (
  patientId: string,
  body: In<typeof requestPatientMergeSchema>,
) =>
  api('POST', `/patients/${patientId}/merge-requests`, { body, schema: patientMergeDetailSchema });

export const patientMergesQuery = (status: PatientMergeStatus) =>
  queryOptions({
    queryKey: ['patient-merges', status],
    queryFn: () =>
      api('GET', `/patient-merge-requests?status=${status}`, { schema: patientMergeListSchema }),
  });

export const patientMergeQuery = (requestId: string) =>
  queryOptions({
    queryKey: ['patient-merges', 'detail', requestId],
    queryFn: () =>
      api('GET', `/patient-merge-requests/${requestId}`, { schema: patientMergeDetailSchema }),
  });

export const decidePatientMerge = (
  requestId: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/patient-merge-requests/${requestId}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: patientMergeDetailSchema,
  });
